import { StyleSheet } from 'aphrodite';
import { SPACE, THEME } from '@Theme';
import {
	background,
	brandPrimary,
	brandPrimaryText,
	brandSecondary,
	darkGrayFontColor,
	dashboardNavBackground,
	destructive,
	label,
	mention,
	mentionDark,
	navigation,
	titles,
	warningDark,
} from '../../../styles/colors';
import { CssSelectors, cardStyle } from '../../../styles/styles';
import { FullscreenModalHeaderHeight } from '../../../components/fullscreen/FullscreenModal/styles';

// The editor beside it takes whatever is left, so this is the only dial that narrows the editor
// without leaving a gap at either edge.
const contextContainerWidth = 480;
const borderBottom = `1px solid ${mentionDark}`;
const borderTop = borderBottom;

export const styleSheet = StyleSheet.create({
	aiContentGenerationWizard: {
		height: '100%',
	},
	aiKitCta: {
		display: 'flex',
		alignItems: 'center',
		margin: '0 24px',
		gap: 4,
		':has(*) > span': {
			textDecoration: 'none',
		},
	},
	approvalStatusTitle: {
		color: warningDark,
		fontSize: 14,
		lineSpacing: '1.1px',
		textAlign: 'left',
		textTransform: 'uppercase',
	},
	approve: {
		marginTop: 10,
	},
	platformLimitNotice: {
		alignItems: 'center',
		backgroundColor: THEME.colors.danger.light,
		borderRadius: THEME.common.borderRadius,
		color: THEME.colors.text.main,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.xs,
		gap: SPACE[8],
		lineHeight: 1.4,
		marginTop: SPACE[8],
		padding: `${SPACE[8]}px ${SPACE[16]}px`,
		textAlign: 'left',
	},
	platformLimitNoticeIcon: {
		display: 'block',
		flexShrink: 0,
	},
	advancedOptions: {
		textAlign: 'left',
		fontSize: 12,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		// Sibling of the questionnaire inside the capped RegEd panel, and the questionnaire is the part
		// meant to give way — this is a toggle, with nothing to scroll and no useful smaller size.
		flexShrink: 0,
		gap: 12,
	},
	buttonContainer: {
		alignItems: 'center',
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
		justifyContent: 'flex-end',
		paddingLeft: 15,
		...CssSelectors.allChildren({
			marginTop: 5,
		}),
	},
	cancel: {
		justifyContent: 'center',
		marginTop: 20,
	},
	card: {
		...cardStyle,
		boxSizing: 'border-box',
		color: label,
		fontSize: 14,
		marginRight: 10,
		marginTop: 20,
		overflow: 'visible',
		padding: '15px 30px',
	},
	cardActions: {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'space-between',
		...CssSelectors.allChildren(
			{
				fontSize: 14,
			},
			'button'
		),
	},
	cardSubSection: {
		alignItems: 'center',
		display: 'flex',
		marginTop: 10,
		...CssSelectors.allChildren({
			height: 24,
			width: 30,
		}),
	},
	regEdComplianceCard: {
		...cardStyle,
		boxSizing: 'border-box',
		// Sibling of the preview in the same flex column: without this it shrinks instead of extending
		// the column, and `cardStyle`'s overflow:hidden then clips the reviewer's note out of sight.
		flexShrink: 0,
		marginRight: 10,
		marginTop: 20,
		// Matches the sibling cards, which override cardStyle for the same reason.
		overflow: 'visible',
		padding: '15px 30px',
	},
	// The RegEd questionnaire runs long enough to push the submit button past the viewport.
	reviseDialogForm: {
		maxHeight: '60vh',
		minHeight: 0,
		overflowY: 'auto',
	},
	// The sidebar is a flex column: a default flex-shrink would compress the preview to fit the
	// cards below rather than letting the column ask the page for the height it needs.
	previewCollapsible: {
		flexShrink: 0,
	},
	// Opens without the shared height animation: inside this sticky, scrolling sidebar Chrome stopped
	// painting the panel once that animation ran, and the preview came back blank.
	previewCollapsiblePanel: {
		transition: 'none',
	},
	previewCollapsibleTrigger: {
		alignItems: 'center',
		background: 'none',
		border: 'none',
		color: brandPrimary,
		cursor: 'pointer',
		display: 'flex',
		fontSize: 14,
		gap: 6,
		justifyContent: 'center',
		margin: '0 auto',
		padding: '8px 0',
	},
	previewCollapsibleChevron: {
		transition: 'transform 150ms ease-out',
	},
	previewCollapsibleChevronOpen: {
		transform: 'rotate(180deg)',
	},
	cardTitle: {
		color: titles,

		fontSize: 16,
		marginBottom: 6,
	},
	charCount: {
		display: 'flex',
		justifyContent: 'flex-end',
		marginRight: 10,
	},
	childRoute: {
		boxSizing: 'border-box',
		height: '100%',
		overflow: 'auto',
		padding: 20,
	},
	citationButton: {
		color: brandPrimaryText,
		marginLeft: 10,
		minWidth: 120,
		padding: '4px 12px',
	},
	composerContainer: {
		border: `1px solid ${mentionDark}`,
		borderRadius: 3,
		boxShadow: `0px 0px 12px ${mention}`,
		boxSizing: 'border-box',
		flex: 1,
		margin: 20,
		// A flex item's automatic minimum size is its content, so without this a single long unbroken
		// line would widen the editor and squeeze the column beside it.
		minWidth: 0,
		'@media (max-width: 768px)': {
			order: 1,
			margin: 10,
			// Stacked, the editor is the whole first screen; this keeps it from collapsing to a short post.
			minHeight: '50vh',
		},
	},
	composerContainerBody: {
		...CssSelectors.allChildren({
			color: brandSecondary,
			fontSize: 18,
			marginRight: 20,
			paddingLeft: 20,
		}),
		'@media (max-width: 768px)': {
			...CssSelectors.allChildren({
				fontSize: 16,
				marginRight: 10,
				paddingLeft: 10,
			}),
		},
	},
	composerContainerHeader: {
		borderBottom,
		boxSizing: 'border-box',
		color: brandPrimaryText,
		display: 'flex',
		flexDirection: 'column',
		fontSize: 24,
		justifyContent: 'center',
		paddingBottom: 16,
		paddingTop: 16,
		'@media (max-width: 768px)': {
			fontSize: 20,
			paddingBottom: 12,
			paddingTop: 12,
		},
	},
	complianceInput: {
		minWidth: 240,
	},
	// The fullscreen router stretches its children to the viewport height, which would cap both
	// columns there and spill their content out of their own borders. Opting out of that stretch and
	// asking for the height as a minimum instead lets this grow to whatever the taller column needs,
	// and leaves the single scrollbar with the router.
	container: {
		alignSelf: 'flex-start',
		boxSizing: 'border-box',
		display: 'flex',
		justifyContent: 'center',
		minHeight: '100%',
		width: '100%',
		'@media (max-width: 768px)': {
			flexDirection: 'column',
		},
	},
	contentContainers: {
		color: navigation,
		fontSize: 14,
		marginRight: 20,
	},
	// The row grows with the editor, so a percentage height collapses to this column's content and
	// clips the bottom-anchored RegEd panel. Viewport height plus sticky keeps its bottom edge on screen.
	contextContainer: {
		alignSelf: 'flex-start',
		display: 'flex',
		flexDirection: 'column',
		// The editor beside it takes the remaining width, so without this the sidebar is what gives way.
		flexShrink: 0,
		height: `calc(100vh - ${FullscreenModalHeaderHeight}px)`,
		overflow: 'auto',
		// Sticky is also the positioning context for the RegEd panel, which the advisor drags up over the preview.
		position: 'sticky',
		top: 0,
		width: contextContainerWidth,
		'@media (max-width: 768px)': {
			// Stacked below the editor, the sidebar is ordinary page content: its own height, in flow.
			alignSelf: 'auto',
			height: 'auto',
			order: 2,
			position: 'relative',
			width: '100%',
			margin: 10,
			marginTop: 0,
		},
	},
	copyLinkField: {
		marginRight: 10,
		minWidth: 168,
		padding: '2px 12px',
	},
	ctaContainer: {
		color: brandPrimaryText,
		flexShrink: 0,
		marginTop: 20,
		textAlign: 'center',
	},
	ctaContainerButtons: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[12],
		marginBottom: 16,
		paddingLeft: 10,
		paddingRight: 10,
		paddingTop: 16,
		'@media (max-width: 768px)': {
			padding: 16,
		},
	},
	// Wraps rather than squeezing: two outline buttons with long labels do not fit a narrow sidebar.
	ctaSecondaryRow: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: SPACE[12],
	},
	ctaSecondaryButton: {
		flex: '1 1 auto',
	},
	ctaPrimaryButton: {
		width: '100%',
	},
	// RegEd only. Anchored to the bottom of the column and drawn OVER the preview rather than taking
	// space from it: dragging the grip up covers the post, so the advisor decides how much of the
	// preview to trade for questionnaire. In flow it could only grow by pushing the column into
	// scrolling, which carried the submit button below the fold. 90vh until a drag sets the property.
	// Scrolling belongs to the questionnaire inside, so the grip and toggle above it stay put.
	ctaContainerRegEd: {
		backgroundColor: background,
		borderTop,
		bottom: 0,
		display: 'flex',
		flexDirection: 'column',
		left: 0,
		marginTop: 0,
		// Up to 90vh, but never past the column it overlays — bottom-anchored, anything taller runs off
		// the top of the sidebar instead of over the preview.
		maxHeight: 'min(var(--reged-host-height, 90vh), 100%)',
		minHeight: 0,
		position: 'absolute',
		right: 0,
		textAlign: 'left',
		// The narrow layout gives the column `height: auto`, and a bottom-anchored overlay against a
		// shrink-wrapped column has nothing to anchor to — it stays in flow there.
		[THEME.queries.md]: {
			maxHeight: 'none',
			position: 'static',
		},
	},
	// A column rather than the base row of buttons, since the only child is the questionnaire — but it
	// has to stay a shrinkable flex item too. As a block it took its content's full height, so the cap
	// above it never reached the scrolling form below and the overflow escaped to the page instead.
	ctaContainerButtonsRegEd: {
		display: 'flex',
		flex: '1 1 auto',
		flexDirection: 'column',
		gap: SPACE[16],
		justifyContent: 'flex-start',
		minHeight: 0,
	},
	// RegEd only: strip the shared prompt's fullscreen-modal styling so it fits the narrow sidebar.
	regEdPromptInSidebar: {
		margin: 0,
		maxWidth: 'none',
		padding: 0,
		textAlign: 'left',
	},
	disclaimerText: {
		fontSize: 12,
		paddingBottom: 10,
		textAlign: 'center',
		width: '60%',
	},
	draftSender: {
		alignItems: 'center',
		background: 'rgba(248, 145, 67, 0.12)',
		color: warningDark,
		display: 'flex',

		fontSize: 12,
		justifyContent: 'center',
		marginTop: 20,
		padding: '12px 0',
	},
	editIcon: {
		marginLeft: 8,
	},
	expirationDate: {
		color: brandPrimaryText,
	},
	expirationDateContainer: {
		alignItems: 'center',
		background: 'rgba(248, 145, 67, 0.12)',
		color: warningDark,
		display: 'flex',

		fontSize: 12,
		justifyContent: 'center',
		padding: '12px 0',
	},
	graphicContainer: {
		alignItems: 'center',
		background: mention,
		color: dashboardNavBackground,
		display: 'flex',
		flex: 1,
		flexDirection: 'column',

		fontSize: 30,
		marginBottom: 10,
		minHeight: 520,
		paddingTop: 30,
		'@media (max-width: 768px)': {
			minHeight: 300,
			fontSize: 24,
			paddingTop: 20,
			marginBottom: 5,
		},
	},
	headerStackContainer: {
		marginLeft: 10,
	},
	heading: {
		minWidth: '28%',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	headingEditor: {
		display: 'flex',
		maxWidth: `calc(100% - 200px)`,
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		'@media (max-width: 768px)': {
			maxWidth: '100%',
			flexWrap: 'wrap',
		},
	},
	headingEditorInput: {
		flexWrap: 'wrap',
		width: 430,
		'@media (max-width: 768px)': {
			width: '100%',
		},
		...CssSelectors.allChildren(
			{
				color: darkGrayFontColor,
				fontSize: 14,
				height: 34,
				marginBottom: 4,
				marginTop: 4,
				padding: '2px 8px',
				textOverflow: 'ellipsis',
			},
			'input'
		),
		...CssSelectors.allChildren(
			{
				marginBottom: 4,
				marginTop: 4,
			},
			'button'
		),
	},
	iconContainer: {
		display: 'flex',
		marginRight: 2,
	},
	iconHide: {
		display: 'none',
	},
	imageDisplayAreaPreview: {
		display: 'flex',
		flexWrap: 'wrap',
		justifyContent: 'center',
		...CssSelectors.allDescendants(
			{
				':last-child': {
					borderBottomLeftRadius: 10,
					borderBottomRightRadius: 10,
				},
			},
			'img'
		),
	},
	infoIcon: {
		padding: 6,
	},
	infoPopover: {
		marginLeft: 2,
	},
	infoPopoverToggle: {
		width: 14,
		height: 14,
	},
	lightBulb: {
		alignSelf: 'flex-start',
		display: 'flex',
		flexShrink: 0,
		marginRight: 6,
		width: 24,
	},
	nameContainer: {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'space-between',
		maxWidth: '100%',
		padding: '4px 20px',
	},
	noAccountsWarning: {
		color: destructive,
		fontSize: 12,
		marginTop: 10,
	},
	popover: {
		borderBottomRightRadius: 0,
		boxSizing: 'border-box',
		color: '#4A4A4A',
		fontSize: 12,
		lineHeight: '18px',
		marginLeft: 14,
		maxWidth: 300,
		padding: '6px 6px 6px 6px',
	},
	popoverContent: {
		borderRadius: 3,
		color: titles,
		fontSize: 12,
		margin: 4,
		padding: 6,
		width: 200,
	},
	postCompleteContainer: {
		padding: 30,
	},
	postFrom: {
		alignItems: 'center',
		display: 'flex',
		marginLeft: 20,
		...CssSelectors.allChildren(
			{
				color: darkGrayFontColor,
				fontSize: 14,
			},
			'span'
		),
	},
	postHeaderContainerLeft: {
		display: 'flex',
		paddingBottom: 4,
		paddingLeft: 24,
	},
	sendOptions: {
		display: 'block',
		minWidth: '100%',
	},
	sloganContainer: {
		paddingBottom: 40,
		paddingTop: 50,
	},
	standAloneButton: {
		marginLeft: 10,
	},
	summary: {
		boxSizing: 'border-box',
		color: navigation,
		display: 'flex',
		fontSize: 14,
		fontStyle: 'italic',
		margin: '4px 40px 8px 20px',
	},
	textAreaContainer: {
		...CssSelectors.allChildren({
			height: 110,
			padding: 14,
		}),
	},
	updateButton: {
		minWidth: 65,
		textAlign: 'center',
	},
	updateCancelContainer: {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'space-between',
	},
	userDropdown: {
		alignItems: 'center',
		color: darkGrayFontColor,
		display: 'flex',
		fontSize: 14,
		marginLeft: 6,
		...CssSelectors.allChildren({
			width: 229,
		}),
	},
	userSelectBox: {
		maxWidth: '35%',
	},
	whereToShare: {
		alignItems: 'center',
		display: 'flex',
		marginTop: 5,
		...CssSelectors.allChildren(
			{
				margin: 0,
			},
			'p'
		),
		...CssSelectors.allChildren(
			{
				color: darkGrayFontColor,
				fontSize: 14,
			},
			'span'
		),
	},
	whereToShareAdmin: {
		marginTop: 20,
	},
	saveTemplateModal: {
		':nth-child(1n) > .modal-overlay': {
			':nth-child(1n) > .modal-overlay-content': {
				width: 540,
			},
		},
	},
	toggle: {},
	logoSizeLabel: {
		color: label,
		// Inline by default, which drops the margin below and leaves the label crowding the toggle.
		display: 'block',
		fontSize: 12,
		marginTop: SPACE[8],
	},
});
